// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {DropMenuEl} from 'common/components/drop-menu';
import {ItemsMenuEl} from 'common/components/items-menu';

import {TagEl} from '../tag';
import {TagSize, TagTheme} from '../tag/types';
import {InputEl} from '../input';
import {InputSize} from '../input/types';
import type {TagInput} from './';
import {TagInputSearchMode} from './types';
import tagStyle from './tag.inline.styl';

function TagMenu(this: TagInput) {
  if (this.state.menuSections === null) {
    return null;
  }

  return (
    <DropMenuEl
      attrs={{
        open: this.state.isMenuOpen,
        'prevent-close-selectors': [`.mp-tag-input-input-wrapper`],
      }}
      on={{
        change: (ev) => this.handleTagMenuChange(ev),
      }}
      props={{
        positionParent:
          this.attr(`search-mode`) === TagInputSearchMode.Hashtag ? this.state.searchInputEl?.searchInput : this,
      }}
      style={this.getDropMenuStyles()}
    >
      {this.state.isMenuOpen && (
        <ItemsMenuEl
          sel='.mp-tag-input-tag-menu'
          attrs={{
            elref: `mpTagInputTagMenu`,
            'search-filter': this.getStagedTagSearch() || ``,
            'empty-message': `No results`,
            ...this.attr(`mp-items-menu-attrs`),
          }}
          props={{
            sections: this.getMenuSections(),
            keydownProxyEl: this.state.searchInputEl,
            sectionsFromInputFn: this.state.sectionsFromInputFn,
          }}
          on={{
            clickItem: (ev) => this.handleClickTagItem(ev),
          }}
        />
      )}
    </DropMenuEl>
  );
}

export function template(this: TagInput) {
  const alwaysShowClear = Boolean(
    this.attr(`show-clear`) && (this.state.tagMap.size > this.attr(`min-tags`) || this.state.inputText),
  );

  const tagEls = (
    <>
      <style>{tagStyle}</style>
      {Array.from(this.state.tagMap.values()).map((tag, index) => {
        return (
          <TagEl
            sel='.mp-tag-input-tag'
            class={{
              multiline: this.attr(`multiline`),
            }}
            attrs={{
              elref: `mpTagInputTag`,
              tabindex: 0,
              removable: this.canRemoveTag(tag),
              size: this.attr(`size`) === InputSize.Small ? TagSize.Mini : TagSize.Small,
              'tag-name': tag.label,
              theme: this.state.isFocused ? TagTheme.Light : TagTheme.Secondary,
              'data-index': index,
              ...tag.attrs,
            }}
            key={`tag-${tag}`}
            on={{
              remove: () => this.canRemoveTag(tag) && this.removeTag(tag),
              keydown: (ev: KeyboardEvent) => this.handleTagKeyDown(ev, {tag, index}),
              click: (ev) => ev.stopPropagation(),
              focus: (ev) => this.handleTagFocus(ev),
              blur: (ev) => this.handleTagBlur(ev),
            }}
          />
        );
      })}
    </>
  );

  return (
    <div sel='.mp-tag-input-container'>
      <InputEl
        sel='.mp-tag-input-search-input'
        attrs={{
          ...this.getInputAttrs(),
          elref: `searchInput`,
          placeholder: this.getPlaceholder(),
          'always-show-clear': alwaysShowClear,
        }}
        style={this.attr(`search-width`) ? {minWidth: `${this.attr(`search-width`)}px`} : {}}
        params={{
          prefixRenderer: () => tagEls,
          suffixRenderer: this.state.suffixRenderer,
        }}
        props={{
          value: this.state.inputText,
        }}
        on={{
          focus: () => this.handleInputFocus(),
          blur: () => this.handleInputBlur(),
          input: (ev) => this.changeInputText(ev.target.value),
          clear: (ev) => this.handleClearInput(ev),
          keydown: (ev) => this.handleInputKeyDown(ev),
        }}
        hook={{
          insert: (vnode) => this.handleSearchInputInsert(vnode),
        }}
      />
      {TagMenu.call(this)}
    </div>
  );
}
